import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Calculator,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderKanban,
  LayoutDashboard,
  type LucideIcon,
  MessageCircle,
  Package,
  User as UserIcon,
  Users,
  Wrench,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { Badge } from '@/components/ui/badge.js';
import { useGetUnreadTotalQuery } from '@/features/chat/chatApi.js';
import { FEATURES } from '@/shared/config/features.js';
import { useReducedMotion } from '@/shared/useReducedMotion.js';
import { cn } from '@/lib/utils.js';
import type { SidebarProps, SidebarItem } from './Sidebar.types.js';

export const SIDEBAR_WIDTH_COLLAPSED = 72;
export const SIDEBAR_WIDTH_EXPANDED = 220;
const STORAGE_KEY = 'sidebar-collapsed';

const NAV_ITEMS: SidebarItem[] = [
  { label: 'Главная', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Проекты', icon: FolderKanban, path: '/projects' },
  { label: 'Калькулятор', icon: Calculator, path: '/calculator' },
  ...(FEATURES.TASKS
    ? [{ label: 'Задачи', icon: ClipboardList, path: '/tasks' } as SidebarItem]
    : []),
  { label: 'Расценки', icon: Wrench, path: '/rates' },
  { label: 'Материалы', icon: Package, path: '/materials' },
  ...(FEATURES.TEAMS ? [{ label: 'Команды', icon: Users, path: '/teams' } as SidebarItem] : []),
  ...(FEATURES.MESSENGER
    ? [
        {
          label: 'Чат',
          icon: MessageCircle,
          path: '/chat',
          badgeKey: 'chat' as const,
        } as SidebarItem,
      ]
    : []),
  { label: 'Профиль', icon: UserIcon, path: '/profile' },
];

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* ignore */
  }
}

export function Sidebar({ variant = 'desktop', onNavigate }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());

  const { data: unreadData } = useGetUnreadTotalQuery(undefined, {
    skip: !FEATURES.MESSENGER,
    pollingInterval: 30000,
  });
  const chatUnread = unreadData?.data?.count ?? 0;

  useEffect(() => {
    persistCollapsed(collapsed);
  }, [collapsed]);

  // mobile sheet variant always shows expanded labels
  const isMobile = variant === 'mobile';
  const showLabels = isMobile || !collapsed;

  return (
    <TooltipProvider delayDuration={150}>
      <aside
        className={cn(
          'relative flex h-full flex-col gap-2 transition-[width] duration-300',
          'glass border-r border-border-subtle',
          isMobile && 'h-full w-full border-r-0',
          !isMobile && (collapsed ? 'w-[72px]' : 'w-[220px]'),
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            'flex items-center gap-2.5 border-b border-border-subtle px-4 py-4',
            !showLabels && 'justify-center px-0',
          )}
        >
          <button
            type="button"
            onClick={() => {
              navigate('/dashboard');
              onNavigate?.();
            }}
            className="shrink-0 rounded-xl shadow-glow-primary transition-transform hover:scale-105"
            aria-label="На главную"
          >
            <img src="/favicon.svg" alt="ПростоСтрой" width={36} height={36} className="h-9 w-9" />
          </button>
          <AnimatePresence initial={false}>
            {showLabels && (
              <motion.span
                key="logo-text"
                initial={reducedMotion ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reducedMotion ? undefined : { opacity: 0, x: -8 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden whitespace-nowrap font-display text-h4 font-bold text-gradient-primary"
              >
                ПростоСтрой
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon as LucideIcon;
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const badge = item.badgeKey === 'chat' && chatUnread > 0 ? chatUnread : 0;

            const link = (
              <NavLink
                to={item.path}
                onClick={() => onNavigate?.()}
                className={cn(
                  'group relative flex h-11 items-center rounded-lg text-small font-medium transition-colors',
                  showLabels ? 'gap-3 px-3' : 'justify-center px-0',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/10',
                )}
              >
                {/* Active indicator with smooth shared layout */}
                {isActive && (
                  <motion.span
                    layoutId="sidebar-active-indicator"
                    className="absolute inset-0 rounded-lg bg-primary/10 ring-1 ring-primary/20"
                    transition={{ type: 'spring', damping: 22, stiffness: 320 }}
                    aria-hidden
                  />
                )}

                <span className="relative flex h-5 w-5 items-center justify-center">
                  <Icon className="h-5 w-5" />
                  {badge > 0 && !showLabels && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1.5 -right-2 h-4 min-w-[16px] rounded-full border-background px-1 text-[10px] font-semibold leading-none"
                    >
                      {badge > 99 ? '99+' : badge}
                    </Badge>
                  )}
                </span>

                <AnimatePresence initial={false}>
                  {showLabels && (
                    <motion.span
                      key="label"
                      initial={reducedMotion ? false : { opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={reducedMotion ? undefined : { opacity: 0, x: -6 }}
                      transition={{ duration: 0.16 }}
                      className="relative flex-1 overflow-hidden whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {showLabels && badge > 0 && (
                  <Badge
                    variant="destructive"
                    className="relative h-5 min-w-[20px] rounded-full px-1.5 text-[10px] font-semibold"
                  >
                    {badge > 99 ? '99+' : badge}
                  </Badge>
                )}
              </NavLink>
            );

            return (
              <div key={item.path}>
                {!showLabels ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  link
                )}
              </div>
            );
          })}
        </nav>

        {/* Collapse toggle (desktop only) */}
        {!isMobile && (
          <div className="border-t border-border-subtle p-2">
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className={cn(
                'flex h-10 w-full items-center justify-center gap-2 rounded-lg',
                'text-muted-foreground transition-colors hover:bg-accent/10 hover:text-foreground',
              )}
              aria-label={collapsed ? 'Развернуть боковую панель' : 'Свернуть боковую панель'}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  <span className="text-xs">Свернуть</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}
